/**
 * Registry statico dei moduli frontend installati nella shell.
 *
 * Ogni modulo esporta il proprio array di route (pattern documentaleRoutes).
 * La shell registra qui ogni modulo al momento dell'integrazione.
 * In questa fase non è necessario Module Federation: la shell
 * importa i sorgenti dei moduli direttamente tramite alias Vite (DEC-014).
 *
 * Ristrutturato secondo DEC-043/044/045/046:
 * - "Costi e margini" smembrato in "Costi fissi", "Ricalcolo margini",
 *   "Dashboard" (break-even), "Andamento prezzi"
 * - Nuovi gruppi: Contabilità (Costi fissi, Ricalcolo), Amministrazione
 *   (Dashboard, Andamento prezzi)
 *
 * Per aggiungere un nuovo modulo:
 *   1. Aggiungere l'alias in vite.config.ts e tailwind.config.ts
 *   2. Importare le route del modulo qui sotto
 *   3. Aggiungerle all'array registeredModules
 */

import React from 'react';
import {
  AlertTriangle,
  ArrowLeftRight,
  BookOpen,
  Boxes,
  Briefcase,
  Building2,
  Calculator,
  CalendarClock,
  ClipboardList,
  Clock,
  FileQuestion,
  FileSearch,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  GitBranch,
  History,
  Landmark,
  Mail,
  MapPin,
  Package,
  Printer,
  Receipt,
  Repeat,
  RefreshCw,
  Settings,
  Shield,
  ShieldAlert,
  ShoppingCart,
  Tag,
  Tags,
  TrendingUp,
  Truck,
  Users,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';
import ClientiList from '../shell/ClientiList';
import ClienteDetail from '../shell/ClienteDetail';
import ClienteForm from '../shell/ClienteForm';
import UtentiList from '../shell/UtentiList';
import UtenteDetail from '../shell/UtenteDetail';
import GruppiList from '../shell/GruppiList';
import ConfigurazioneAdmin from '../shell/ConfigurazioneAdmin';
import GestioneModuli from '../shell/GestioneModuli';
import ImpostazioniAzienda from '../shell/ImpostazioniAzienda';
/* PermessiAdmin rimosso dalla sidebar — i permessi si gestiscono da
   Configurazione (tab Ruoli), Gruppi e dettaglio utente */
import {
  FattureList,
  FatturaDetail,
  FatturaForm,
  ArchivioExplorer,
  Scadenzario,
  RegistroIva,
  VerificheList,
  VerificheRegolaDetail,
} from '@floraltech/documentale';
import { EmailLayout, AccountSettings as EmailAccountSettings, RegolePage as EmailRegolePage } from '@floraltech/email';
import { ContrattiList, ContrattoDetail, ContrattoNuovo } from '@floraltech/contratti';
import { FornitoriList, FornitoreDetail, FornitoreForm } from '@floraltech/fornitori';
import {
  MagazzinoOverview,
  ArticoliList,
  ArticoliBrowser,
  CategorieManager,
  ArticoloDetail,
  ArticoloForm,
  PosizioniList,
  CaricoForm,
  MovimentiList,
  LottiList,
  LottoDetail,
} from '@floraltech/magazzino';
import {
  OrdiniList,
  NuovoOrdine,
  OrdineDetail,
  RicezioneForm,
  ContenziosiList,
  ContenziosoDetail,
  PreventiviList,
  NuovoPreventivo,
  NuovoPreventivoVendita,
  PreventivoAcquistoRouter,
  PreventivoVenditaRouter,
  RedirectNuovoPreventivoVendita,
  OrdineVenditaList,
  OrdineVenditaDetail,
  DDTList,
  DDTDetail,
  NuovoDDT,
  DocumentiList,
  DocumentoDetail,
  NuovoDocumento,
  RichiesteList,
  NuovaRichiesta,
  RichiestaDetail,
} from '@floraltech/ordini';
import {
  CassaOverview,
  PuntoVendita,
  ScontriniList,
  ScontrinoDetail,
  TurniList,
  TurnoDetail,
  ResoForm,
  RegistroCassa,
  RepartiRT,
  ConfigurazioneRT,
} from '@floraltech/cassa';
import {
  AndamentoPrezzi,
  CostiFissiList,
  ListinoPrezzi,
  RicalcoloForm,
  StoricoRicalcoli,
  BreakEvenDashboard,
} from '@floraltech/prezzi';
import {
  ContiList,
  ContoDetail,
  BonificiList,
  NuovoBonifico,
} from '@floraltech/banca';
import {
  CentriCostoTree,
  CentroCostoDetail,
  CommesseList,
  CommessaDetail,
  ImputazioniList,
  NuovaImputazione,
} from '@floraltech/centri_costo';

/** Definizione di una singola route del registry. */
export interface ModuleRoute {
  /** Path React Router (es. '/documentale/fatture'). */
  path: string;
  /** Componente React da montare. */
  component: React.ComponentType;
  /** Etichetta per la navigazione sidebar. Omessa se hidden=true. */
  label?: string;
  /** Componente icona Lucide della voce sidebar (riferimento diretto). */
  icon?: LucideIcon;
  /** Se true, la route non compare nella navigazione laterale. */
  hidden?: boolean;
}

/** Definizione di un modulo nel registry. */
export interface ModuleEntry {
  /** Nome del modulo (usato come section header nella sidebar). */
  nome: string;
  /** Route del modulo. */
  routes: ModuleRoute[];
  /**
   * Nome del modulo backend corrispondente (match ModuleManifest.nome).
   * Usato per il filtro sidebar per ramo operativo.
   * Se omesso, il modulo è sempre visibile (es. moduli Sistema).
   */
  backendModulo?: string;
}

/**
 * Elenco di tutti i moduli frontend installati nella shell.
 * Ordinato per priorità di visualizzazione nella sidebar.
 */
export const registeredModules: ModuleEntry[] = [
  // --- Vendite ---
  {
    nome: 'Cassa',
    backendModulo: 'cassa',
    routes: [
      {
        path: '/cassa',
        component: CassaOverview,
        label: 'Dashboard',
        icon: ShoppingCart,
      },
      {
        path: '/cassa/vendita',
        component: PuntoVendita,
        label: 'Punto vendita',
        icon: ShoppingCart,
      },
      {
        path: '/cassa/scontrini',
        component: ScontriniList,
        label: 'Scontrini',
        icon: Receipt,
      },
      {
        path: '/cassa/scontrini/:id',
        component: ScontrinoDetail,
        hidden: true,
      },
      {
        path: '/cassa/turni',
        component: TurniList,
        label: 'Turni',
        icon: Clock,
      },
      {
        path: '/cassa/turni/:id',
        component: TurnoDetail,
        hidden: true,
      },
      {
        path: '/cassa/reso',
        component: ResoForm,
        hidden: true,
      },
      {
        path: '/cassa/registro',
        component: RegistroCassa,
        label: 'Registro',
        icon: Receipt,
      },
      {
        path: '/cassa/reparti-rt',
        component: RepartiRT,
        hidden: true,
      },
      {
        path: '/cassa/registratore',
        component: ConfigurazioneRT,
        label: 'Registratore',
        icon: Printer,
      },
    ],
  },
  {
    nome: 'Clienti',
    routes: [
      {
        path: '/clienti',
        component: ClientiList,
        label: 'Clienti',
        icon: Users,
      },
      {
        path: '/clienti/nuovo',
        component: ClienteForm,
        hidden: true,
      },
      {
        path: '/clienti/:id',
        component: ClienteDetail,
        hidden: true,
      },
      {
        path: '/clienti/:id/modifica',
        component: ClienteForm,
        hidden: true,
      },
    ],
  },
  // --- Magazzino ---
  {
    nome: 'Magazzino',
    backendModulo: 'magazzino',
    routes: [
      {
        path: '/magazzino',
        component: MagazzinoOverview,
        label: 'Dashboard',
        icon: Warehouse,
      },
      {
        path: '/magazzino/articoli',
        component: ArticoliBrowser,
        label: 'Articoli',
        icon: Package,
      },
      {
        path: '/magazzino/categorie',
        component: CategorieManager,
        label: 'Categorie',
        icon: Tags,
      },
      {
        path: '/magazzino/articoli/nuovo',
        component: ArticoloForm,
        hidden: true,
      },
      {
        path: '/magazzino/articoli/:id',
        component: ArticoloDetail,
        hidden: true,
      },
      {
        path: '/magazzino/articoli/:id/modifica',
        component: ArticoloForm,
        hidden: true,
      },
      {
        path: '/magazzino/posizioni',
        component: PosizioniList,
        label: 'Posizioni',
        icon: MapPin,
      },
      {
        path: '/magazzino/carico',
        component: CaricoForm,
        hidden: true,
      },
      {
        path: '/magazzino/movimenti',
        component: MovimentiList,
        label: 'Movimenti',
        icon: ArrowLeftRight,
      },
      {
        path: '/magazzino/lotti',
        component: LottiList,
        label: 'Lotti',
        icon: Boxes,
      },
      {
        path: '/magazzino/lotti/:lottoId',
        component: LottoDetail,
        hidden: true,
      },
    ],
  },
  // --- Acquisti ---
  {
    nome: 'Fornitori',
    backendModulo: 'ordini',
    routes: [
      {
        path: '/fornitori',
        component: FornitoriList,
        label: 'Fornitori',
        icon: Truck,
      },
      {
        path: '/fornitori/nuovo',
        component: FornitoreForm,
        hidden: true,
      },
      {
        path: '/fornitori/:id',
        component: FornitoreDetail,
        hidden: true,
      },
      {
        path: '/fornitori/:id/modifica',
        component: FornitoreForm,
        hidden: true,
      },
    ],
  },
  {
    nome: 'Richieste',
    backendModulo: 'ordini',
    routes: [
      {
        path: '/richieste',
        component: RichiesteList,
        label: 'Richieste',
        icon: FileQuestion,
      },
      {
        path: '/richieste/nuova',
        component: NuovaRichiesta,
        hidden: true,
      },
      {
        path: '/richieste/:id',
        component: RichiestaDetail,
        hidden: true,
      },
    ],
  },
  {
    // Ciclo PASSIVO: preventivi ricevuti dai fornitori (RdO -> preventivo -> ordine acquisto).
    nome: 'Preventivi fornitori',
    backendModulo: 'ordini',
    routes: [
      {
        path: '/preventivi',
        component: PreventiviList,
        label: 'Preventivi fornitori',
        icon: FileSearch,
      },
      {
        path: '/preventivi/nuovo',
        component: NuovoPreventivo,
        hidden: true,
      },
      {
        // Link legacy: la creazione vendita ora vive sotto /vendite/preventivi/nuovo.
        path: '/preventivi/vendita/nuovo',
        component: RedirectNuovoPreventivoVendita,
        hidden: true,
      },
      {
        // Wrapper tipo-aware: se il preventivo e' di vendita, redirect a /vendite/preventivi/:id.
        path: '/preventivi/:id',
        component: PreventivoAcquistoRouter,
        hidden: true,
      },
    ],
  },
  {
    // Ciclo ATTIVO: preventivi per i clienti (preventivo cliente -> ordine cliente -> DDT -> fattura).
    nome: 'Preventivi clienti',
    backendModulo: 'ordini',
    routes: [
      {
        path: '/vendite/preventivi',
        component: PreventiviList,
        label: 'Preventivi clienti',
        icon: FileSearch,
      },
      {
        path: '/vendite/preventivi/nuovo',
        component: NuovoPreventivoVendita,
        hidden: true,
      },
      {
        // Wrapper tipo-aware: se il preventivo e' di acquisto, redirect a /preventivi/:id.
        path: '/vendite/preventivi/:id',
        component: PreventivoVenditaRouter,
        hidden: true,
      },
    ],
  },
  {
    nome: 'Ordini vendita',
    backendModulo: 'ordini',
    routes: [
      {
        path: '/ordini-vendita',
        component: OrdineVenditaList,
        label: 'Ordini vendita',
        icon: ShoppingCart,
      },
      {
        path: '/ordini-vendita/:id',
        component: OrdineVenditaDetail,
        hidden: true,
      },
    ],
  },
  {
    nome: 'DDT e documenti',
    backendModulo: 'ordini',
    routes: [
      {
        path: '/ddt',
        component: DDTList,
        label: 'DDT',
        icon: Truck,
      },
      {
        path: '/ddt/nuovo',
        component: NuovoDDT,
        hidden: true,
      },
      {
        path: '/ddt/:id',
        component: DDTDetail,
        hidden: true,
      },
      {
        path: '/documenti-commerciali',
        component: DocumentiList,
        label: 'Documenti',
        icon: FileText,
      },
      {
        path: '/documenti-commerciali/nuovo',
        component: NuovoDocumento,
        hidden: true,
      },
      {
        path: '/documenti-commerciali/:id',
        component: DocumentoDetail,
        hidden: true,
      },
    ],
  },
  {
    nome: 'Ordini',
    backendModulo: 'ordini',
    routes: [
      {
        path: '/ordini',
        component: OrdiniList,
        label: 'Ordini',
        icon: ClipboardList,
      },
      {
        path: '/ordini/nuovo',
        component: NuovoOrdine,
        hidden: true,
      },
      {
        path: '/ordini/:id',
        component: OrdineDetail,
        hidden: true,
      },
      {
        path: '/ordini/ricezioni/:ricezioneId',
        component: RicezioneForm,
        hidden: true,
      },
      {
        path: '/ordini/contenziosi',
        component: ContenziosiList,
        label: 'Contenziosi',
        icon: AlertTriangle,
      },
      {
        path: '/ordini/contenziosi/:id',
        component: ContenziosoDetail,
        hidden: true,
      },
    ],
  },
  // --- Contabilità ---
  {
    nome: 'Fatturazione',
    backendModulo: 'documentale',
    routes: [
      {
        path: '/documentale/fatture',
        component: FattureList,
        label: 'Fatture',
        icon: FileText,
      },
      {
        path: '/documentale/fatture/nuova',
        component: FatturaForm,
        hidden: true,
      },
      {
        // Correzione di una bozza: stessa pagina della creazione, con l'id.
        // Va dichiarata PRIMA di '/:id' per leggibilita', anche se col matching
        // di React Router 6 l'ordine non e' vincolante (vince il piu'
        // specifico). Il registro che conta per il routing e' QUESTO file:
        // le rotte dichiarate in modules/<x>/frontend/src/index.ts non vengono
        // montate, e una rotta aggiunta solo li' produce una pagina bianca.
        path: '/documentale/fatture/:id/modifica',
        component: FatturaForm,
        hidden: true,
      },
      {
        path: '/documentale/fatture/:id',
        component: FatturaDetail,
        hidden: true,
      },
      {
        path: '/documentale/scadenzario',
        component: Scadenzario,
        label: 'Scadenzario',
        icon: CalendarClock,
      },
      {
        path: '/documentale/registro-iva',
        component: RegistroIva,
        label: 'Registro IVA',
        icon: BookOpen,
      },
    ],
  },
  {
    nome: 'Verifiche fiscali',
    backendModulo: 'documentale',
    routes: [
      {
        path: '/documentale/verifiche',
        component: VerificheList,
        label: 'Verifiche fiscali',
        icon: ShieldAlert,
      },
      {
        path: '/documentale/verifiche/:codice',
        component: VerificheRegolaDetail,
        hidden: true,
      },
    ],
  },
  {
    nome: 'Ricalcolo margini',
    backendModulo: 'prezzi',
    routes: [
      {
        path: '/prezzi/ricalcolo',
        component: RicalcoloForm,
        label: 'Ricalcolo',
        icon: RefreshCw,
      },
      {
        path: '/prezzi/listino',
        component: ListinoPrezzi,
        label: 'Listino',
        icon: Tag,
      },
      {
        path: '/prezzi/costi-fissi',
        component: CostiFissiList,
        hidden: true,
      },
      {
        path: '/prezzi/storico',
        component: StoricoRicalcoli,
        hidden: true,
      },
    ],
  },
  {
    nome: 'Conti',
    backendModulo: 'banca',
    routes: [
      {
        path: '/banca/conti',
        component: ContiList,
        label: 'Conti',
        icon: Landmark,
      },
      {
        path: '/banca/conti/:contoId',
        component: ContoDetail,
        hidden: true,
      },
      {
        path: '/banca/callback',
        component: ContiList,
        hidden: true,
      },
    ],
  },
  {
    nome: 'Bonifici',
    backendModulo: 'banca',
    routes: [
      {
        path: '/banca/bonifici',
        component: BonificiList,
        label: 'Bonifici',
        icon: Landmark,
      },
      {
        path: '/banca/bonifici/nuovo',
        component: NuovoBonifico,
        hidden: true,
      },
    ],
  },
  // --- Contabilità analitica ---
  {
    nome: 'Centri di costo',
    backendModulo: 'centri_costo',
    routes: [
      { path: '/centri-costo/centri', component: CentriCostoTree, label: 'Centri di costo', icon: GitBranch },
      { path: '/centri-costo/centri/:id', component: CentroCostoDetail, hidden: true },
    ],
  },
  {
    nome: 'Commesse',
    backendModulo: 'centri_costo',
    routes: [
      { path: '/centri-costo/commesse', component: CommesseList, label: 'Commesse', icon: Briefcase },
      { path: '/centri-costo/commesse/:id', component: CommessaDetail, hidden: true },
    ],
  },
  {
    nome: 'Imputazioni',
    backendModulo: 'centri_costo',
    routes: [
      { path: '/centri-costo/imputazioni', component: ImputazioniList, label: 'Imputazioni', icon: FileSpreadsheet },
      { path: '/centri-costo/imputazioni/nuova', component: NuovaImputazione, hidden: true },
    ],
  },
  // --- Canoni ricorrenti ---
  {
    nome: 'Contratti clienti',
    backendModulo: 'contratti',
    routes: [
      { path: '/vendite/contratti', component: ContrattiList, label: 'Contratti clienti', icon: Repeat },
      { path: '/vendite/contratti/nuovo', component: ContrattoNuovo, hidden: true },
      { path: '/vendite/contratti/:id', component: ContrattoDetail, hidden: true },
    ],
  },
  {
    nome: 'Contratti fornitori',
    backendModulo: 'contratti',
    routes: [
      { path: '/acquisti/contratti', component: ContrattiList, label: 'Contratti fornitori', icon: Repeat },
      { path: '/acquisti/contratti/nuovo', component: ContrattoNuovo, hidden: true },
      { path: '/acquisti/contratti/:id', component: ContrattoDetail, hidden: true },
      { path: '/contratti', component: ContrattiList, hidden: true },
      { path: '/contratti/nuovo', component: ContrattoNuovo, hidden: true },
      { path: '/contratti/:id', component: ContrattoDetail, hidden: true },
    ],
  },
  // --- Documenti ---
  {
    nome: 'Archivio',
    backendModulo: 'documentale',
    routes: [
      {
        path: '/documentale/archivio',
        component: ArchivioExplorer,
        label: 'Archivio',
        icon: FolderOpen,
      },
    ],
  },
  // --- Comunicazioni ---
  {
    nome: 'Email',
    backendModulo: 'email',
    routes: [
      {
        path: '/email',
        component: EmailLayout,
        label: 'Posta',
        icon: Mail,
      },
      {
        path: '/email/impostazioni',
        component: EmailAccountSettings,
        hidden: true,
      },
      {
        path: '/email/regole',
        component: EmailRegolePage,
        hidden: true,
      },
    ],
  },
  // --- Amministrazione ---
  {
    nome: 'Dashboard',
    routes: [
      {
        path: '/prezzi',
        component: BreakEvenDashboard,
        label: 'Break-even',
        icon: TrendingUp,
      },
    ],
  },
  {
    nome: 'Andamento prezzi',
    routes: [
      {
        path: '/prezzi/andamento',
        component: AndamentoPrezzi,
        label: 'Andamento prezzi',
        icon: History,
      },
    ],
  },
  // --- Sistema ---
  {
    nome: 'Utenti',
    routes: [
      {
        path: '/admin/utenti',
        component: UtentiList,
        label: 'Utenti',
        icon: Users,
      },
      {
        path: '/admin/utenti/:id',
        component: UtenteDetail,
        hidden: true,
      },
    ],
  },
  {
    nome: 'Gruppi',
    routes: [
      {
        path: '/admin/gruppi',
        component: GruppiList,
        label: 'Gruppi',
        icon: Shield,
      },
    ],
  },
  /* Permessi rimosso dalla sidebar — gestito in Configurazione tab Ruoli,
     dettaglio Gruppi e dettaglio Utente */
  {
    nome: 'Azienda',
    routes: [
      {
        path: '/admin/azienda',
        component: ImpostazioniAzienda,
        label: 'Azienda',
        icon: Building2,
      },
    ],
  },
  {
    nome: 'Moduli',
    routes: [
      {
        path: '/admin/moduli',
        component: GestioneModuli,
        label: 'Moduli',
        icon: Package,
      },
    ],
  },
  {
    nome: 'Configurazione',
    routes: [
      {
        path: '/admin/configurazione',
        component: ConfigurazioneAdmin,
        label: 'Configurazione',
        icon: Settings,
      },
    ],
  },
];
